import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, fmt$, STATUS_COLORS } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Clock, MapPin, DollarSign, Share2, Copy } from "lucide-react";

export default function BookingStatus() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [b, setB] = useState(null);
  const [promo, setPromo] = useState("");
  const [tip, setTip] = useState(0);

  const load = useCallback(async () => {
    const { data } = await api.get(`/booking/${token}`);
    setB(data);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // If returning from Stripe with session_id, poll status
    const sid = params.get("session_id");
    const paidKind = params.get("paid");
    if (sid && paidKind) {
      let tries = 0;
      const iv = setInterval(async () => {
        tries++;
        try {
          const { data } = await api.get(`/payments/status/${sid}`);
          if (data.payment_status === "paid") {
            clearInterval(iv);
            toast.success(`${paidKind} received`);
            load();
          }
        } catch {}
        if (tries > 15) clearInterval(iv);
      }, 2000);
      return () => clearInterval(iv);
    }
  }, [params, load]);

  const applyPromo = async () => {
    try {
      const { data } = await api.post(`/booking/${token}/apply-promo`, { code: promo });
      toast.success("Promo applied");
      setB({ ...b, totals: data, promo_code: promo.toUpperCase() });
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
  };

  const acceptTime = async () => {
    try {
      await api.post(`/booking/${token}/accept-time`);
      toast.success("Time accepted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const checkout = async (kind, amount) => {
    try {
      const { data } = await api.post("/payments/checkout", {
        magic_token: token, kind, tip_amount: amount, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (!b) return <div className="max-w-2xl mx-auto px-4 py-12 text-zinc-400">Loading...</div>;

  const t = b.totals || {};
  const balance = Math.max(0, (t.total || 0) - (b.amount_paid || 0));

  return (
    <div className="max-w-2xl mx-auto px-4 py-10 space-y-5">
      <div className="text-xs uppercase tracking-widest text-[#CCFF00]">Your Booking</div>
      <h1 className="font-display text-4xl">{t.service_name}</h1>
      <span className={`status-pill ${STATUS_COLORS[b.status] || ""}`} data-testid="booking-status-pill">{b.status}</span>

      <div className="card-surface p-5 space-y-2 text-sm">
        <div className="flex items-center gap-2 text-zinc-300"><MapPin size={14} /> {b.address}</div>
        <div className="flex items-center gap-2 text-zinc-300">
          <Clock size={14} />
          {b.confirmed_date || b.proposed_date || b.preferred_date} at {b.confirmed_time || b.proposed_time || b.preferred_time}
        </div>
        <div className="text-zinc-400">{b.vehicle_year} {b.vehicle_make} {b.vehicle_model} · {b.vehicle_size} · {b.condition}</div>
        {b.assigned_employee_name && <div className="text-zinc-300 text-xs">Detailer assigned: {b.assigned_employee_name}</div>}
      </div>

      {b.status === "Alternate Time Proposed" && (
        <div className="card-surface p-5 border-blue-500/40 border-l-4">
          <div className="text-xs uppercase tracking-widest text-blue-300">New time proposed</div>
          <div className="font-display text-2xl mt-1">{b.proposed_date} at {b.proposed_time}</div>
          <button data-testid="accept-proposed-btn" onClick={acceptTime} className="neon-btn mt-4 px-5 py-3 rounded-xl">
            Accept proposed time
          </button>
        </div>
      )}

      <div className="card-surface p-5 space-y-2">
        <div className="flex justify-between"><span>Subtotal</span><span>{fmt$(t.subtotal)}</span></div>
        {t.discount > 0 && <div className="flex justify-between text-[#CCFF00]"><span>Discount</span><span>-{fmt$(t.discount)}</span></div>}
        <div className="flex justify-between font-semibold border-t border-zinc-800 pt-2">
          <span>Total</span><span>{fmt$(t.total)}</span>
        </div>
        <div className="flex justify-between text-sm text-zinc-400">
          <span>Paid</span><span>{fmt$(b.amount_paid)}</span>
        </div>
        <div className="flex justify-between text-sm text-zinc-400">
          <span>Balance</span><span>{fmt$(balance)}</span>
        </div>
      </div>

      {b.payment && (
        <div className="card-surface p-5" data-testid="customer-payment-summary">
          <div className="text-xs uppercase tracking-widest text-[#CCFF00] mb-2">Payment</div>
          <div className="flex justify-between text-sm"><span>Service Total</span><span>{fmt$(b.payment.service_total)}</span></div>
          {b.payment.deposit_waived ? (
            <div className="mt-2 rounded-lg bg-fuchsia-500/10 border border-fuchsia-500/40 px-3 py-2 text-fuchsia-200 text-sm" data-testid="deposit-waived-banner">
              Deposit waived — your appointment is confirmed.
            </div>
          ) : (
            b.payment.total_collected > 0 && (
              <div className="flex justify-between text-sm text-zinc-400 mt-1"><span>Paid</span><span>{fmt$(b.payment.total_collected)}</span></div>
            )
          )}
          <div className="flex justify-between text-sm text-zinc-300 mt-1"><span>Remaining Balance</span><span>{fmt$(b.payment.balance_due)}</span></div>
          <div className="flex justify-between text-sm mt-2">
            <span className="text-zinc-500">Status</span>
            <span data-testid="customer-payment-status" className="text-[#CCFF00]">{b.payment.payment_status}</span>
          </div>
        </div>
      )}

      {!b.deposit_paid && !b.payment?.deposit_waived && !b.totals?.waives_deposit && (
        <div className="card-surface p-5">
          <div className="flex gap-2 mb-3">
            <input data-testid="promo-code-input" placeholder="Promo code" value={promo}
              onChange={(e) => setPromo(e.target.value)}
              className="flex-1 px-4 py-3 rounded-xl border border-zinc-700 outline-none focus:border-[#CCFF00]" />
            <button data-testid="apply-promo-btn" onClick={applyPromo} className="border border-zinc-700 hover:border-[#CCFF00] rounded-xl px-4">Apply</button>
          </div>
        </div>
      )}

      {(b.status === "Awaiting Deposit" || (b.status === "Confirmed" && !b.deposit_paid)) && !b.totals?.waives_deposit && !b.payment?.deposit_waived && (
        <button data-testid="pay-deposit-btn" onClick={() => checkout("deposit")} className="neon-btn w-full py-4 rounded-xl">
          <DollarSign size={16} className="inline -mt-1" /> Pay deposit {fmt$(t.deposit)}
        </button>
      )}

      {b.deposit_paid && !b.balance_paid && (b.status === "Confirmed" || b.status === "In Progress" || b.status === "Completed") && balance > 0 && (
        <button data-testid="pay-balance-btn" onClick={() => checkout("balance")} className="neon-btn w-full py-4 rounded-xl">
          Pay remaining balance {fmt$(balance)}
        </button>
      )}

      {b.balance_paid && !b.tip_paid && (
        <div className="card-surface p-5">
          <div className="text-xs uppercase tracking-widest text-[#CCFF00] mb-2">Leave a tip</div>
          <div className="flex gap-2 flex-wrap">
            {[5, 10, 15, 20].map((v) => (
              <button key={v} data-testid={`tip-${v}`} onClick={() => setTip(v)}
                className={`px-4 py-3 rounded-xl border ${tip === v ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "border-zinc-700"}`}>
                {fmt$(v)}
              </button>
            ))}
            <input data-testid="tip-custom" type="number" placeholder="Custom" value={tip}
              onChange={(e) => setTip(Number(e.target.value))}
              className="px-3 py-2 rounded-xl border border-zinc-700 w-28" />
          </div>
          <button data-testid="submit-tip-btn" disabled={!tip} onClick={() => checkout("tip", tip)}
            className="neon-btn w-full py-4 rounded-xl mt-3">Send tip</button>
        </div>
      )}

      {b.tip_paid && (
        <div className="card-surface p-5 flex items-center gap-3 text-[#CCFF00]">
          <CheckCircle2 /> Thank you - your detailer appreciates it.
        </div>
      )}

      {b.referral && (
        <div className="card-surface p-5" data-testid="referral-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#CCFF00]">
            <Share2 size={14} /> Refer a friend, save $10
          </div>
          <p className="text-sm text-zinc-300 mt-2">
            Share your code — friends get $10 off their first detail, you get $10 in credit on your next one.
          </p>
          {b.referral.credit > 0 && (
            <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#CCFF00]/10 border border-[#CCFF00]/40 px-3 py-1 text-xs text-[#CCFF00]" data-testid="referral-credit">
              You have {fmt$(b.referral.credit)} in credit
            </div>
          )}
          <div className="mt-4 flex items-center gap-2">
            <div className="flex-1 bg-black/40 rounded-xl border border-zinc-700 px-3 py-3 font-mono text-sm break-all" data-testid="referral-code">
              {b.referral.code}
            </div>
            <button data-testid="copy-referral-btn"
              onClick={() => {
                const url = `${window.location.origin}/book?ref=${b.referral.code}`;
                navigator.clipboard.writeText(url).then(() => toast.success("Share link copied"));
              }}
              className="border border-zinc-700 hover:border-[#CCFF00] rounded-xl px-3 py-3">
              <Copy size={16} />
            </button>
          </div>
          <div className="text-xs text-zinc-500 mt-2 break-all">
            {typeof window !== "undefined" ? `${window.location.origin}/book?ref=${b.referral.code}` : ""}
          </div>
        </div>
      )}
    </div>
  );
}
